import React from 'react';
import {
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Typography,
  Button,
  Box,
  Chip,
  LinearProgress,
  Stack,
} from '@mui/material';
import {
  PlayCircleOutline,
  CheckCircle,
  School,
  Schedule,
  EmojiEvents,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { TrainingCourse } from '../services/trainingService';

interface TrainingCourseCardProps {
  course: TrainingCourse;
}

export default function TrainingCourseCard({ course }: TrainingCourseCardProps) {
  const navigate = useNavigate();

  const progress = course.userProgress;
  const isCompleted = progress?.status === 'COMPLETED';
  const isInProgress = progress?.status === 'IN_PROGRESS';
  const isEnrolled = !!progress;

  const progressPercentage = progress
    ? Math.round((progress.videoProgress / progress.videoDuration) * 100) || 0
    : 0;

  const handleClick = () => {
    navigate(`/training/courses/${course.id}`);
  };

  const getDifficultyColor = (level: string) => {
    switch (level) {
      case 'BEGINNER':
        return 'success';
      case 'INTERMEDIATE':
        return 'warning';
      case 'ADVANCED':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 6,
        },
        cursor: 'pointer',
      }}
      onClick={handleClick}
    >
      {/* Thumbnail */}
      <CardMedia
        component="div"
        sx={{
          height: 180,
          bgcolor: 'primary.main',
          backgroundImage: course.thumbnailUrl
            ? `url(${course.thumbnailUrl})`
            : 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          position: 'relative',
        }}
      >
        {/* Status Badge */}
        <Box
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
          }}
        >
          {isCompleted && (
            <Chip
              icon={<CheckCircle />}
              label="Completed"
              color="success"
              size="small"
            />
          )}
          {isInProgress && (
            <Chip
              icon={<PlayCircleOutline />}
              label="In Progress"
              color="primary"
              size="small"
            />
          )}
          {course.isRequired && !isCompleted && (
            <Chip label="Required" color="error" size="small" />
          )}
          {course.isRecommended && !course.isRequired && !isCompleted && (
            <Chip label="Recommended" color="warning" size="small" />
          )}
        </Box>
      </CardMedia>

      {/* Content */}
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography variant="h6" gutterBottom fontWeight="bold" noWrap>
          {course.title}
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          gutterBottom
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            mb: 2,
          }}
        >
          {course.description}
        </Typography>

        {/* Course Info */}
        <Stack direction="row" spacing={1} flexWrap="wrap" gap={1} mb={2}>
          <Chip
            icon={<School />}
            label={course.difficultyLevel}
            size="small"
            color={getDifficultyColor(course.difficultyLevel) as any}
            variant="outlined"
          />
          <Chip
            icon={<Schedule />}
            label={`${course.estimatedDuration} min`}
            size="small"
            variant="outlined"
          />
          {course.category && (
            <Chip label={course.category} size="small" variant="outlined" />
          )}
        </Stack>

        {/* Progress Bar */}
        {isInProgress && (
          <Box mb={1}>
            <Box display="flex" justifyContent="space-between" mb={0.5}>
              <Typography variant="caption" color="text.secondary">
                Progress
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {progressPercentage}%
              </Typography>
            </Box>
            <LinearProgress variant="determinate" value={progressPercentage} />
          </Box>
        )}

        {/* Completed Info */}
        {isCompleted && progress?.completedAt && (
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <EmojiEvents sx={{ fontSize: 18, color: 'success.main' }} />
            <Typography variant="caption" color="text.secondary">
              Completed {new Date(progress.completedAt).toLocaleDateString()}
            </Typography>
          </Box>
        )}
      </CardContent>

      {/* Actions */}
      <CardActions sx={{ px: 2, pb: 2 }}>
        <Button
          fullWidth
          variant={isCompleted ? 'outlined' : 'contained'}
          startIcon={
            isCompleted ? (
              <CheckCircle />
            ) : isInProgress ? (
              <PlayCircleOutline />
            ) : (
              <School />
            )
          }
          onClick={handleClick}
        >
          {isCompleted
            ? 'View Certificate'
            : isInProgress
            ? 'Resume Course'
            : isEnrolled
            ? 'Continue'
            : 'Start Course'}
        </Button>
      </CardActions>
    </Card>
  );
}

